import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { Recipe } from "@/lib/types";
import { Facebook, Twitter, Share2 } from "lucide-react";

interface ShareButtonsProps {
  readonly recipe: Recipe;
  readonly shareUrl: string;
}

export function ShareButtons({ recipe, shareUrl }: ShareButtonsProps) {
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(recipe.title);
  const encodedDescription = encodeURIComponent(
    recipe.notes || `Check out this recipe: ${recipe.title}`
  );

  const handleFacebookShare = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    window.open(url, "_blank", "width=600,height=400");
  };

  const handleTwitterShare = () => {
    const text = `Check out this recipe: ${encodedTitle}`;
    const url = `https://twitter.com/intent/tweet?text=${text}&url=${encodedUrl}`;
    window.open(url, "_blank", "width=600,height=400");
  };

  const handlePinterestShare = () => {
    const imageUrl = recipe.imageUrl ? encodeURIComponent(recipe.imageUrl) : "";
    const url = `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedDescription}&media=${imageUrl}`;
    window.open(url, "_blank", "width=600,height=400");
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: recipe.title,
          text: recipe.notes || `Check out this recipe: ${recipe.title}`,
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled or error occurred
        console.log("Share cancelled or failed:", err);
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Share2 className="h-5 w-5" />
          Share Recipe
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={handleFacebookShare}
            className="gap-2"
          >
            <Facebook className="h-4 w-4" />
            Facebook
          </Button>
          <Button
            variant="outline"
            onClick={handleTwitterShare}
            className="gap-2"
          >
            <Twitter className="h-4 w-4" />
            Twitter
          </Button>
          <Button
            variant="outline"
            onClick={handlePinterestShare}
            className="gap-2"
          >
            <Share2 className="h-4 w-4" />
            Pinterest
          </Button>
          {typeof navigator !== "undefined" && typeof navigator.share === "function" && (
            <Button
              variant="outline"
              onClick={handleNativeShare}
              className="gap-2"
            >
              <Share2 className="h-4 w-4" />
              Share
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

